import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { officesAPI, ministriesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

interface OfficeItem {
  id: number;
  name: string;
  address?: string | null;
  ministry_id: number;
  isActive?: boolean;
}

const Offices: React.FC = () => {
  const { user } = useAuth();
  const [_, setMinistries] = useState<Array<{ id: number; name: string }>>([]);
  const [ministryId, setMinistryId] = useState<number | null>(null);
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [admins, setAdmins] = useState<Array<{ id: number; fullName: string; email: string; phoneNumber?: string; officeName?: string }>>([]);

  // Create Office form state
  const [officeName, setOfficeName] = useState('');
  const [officeAddress, setOfficeAddress] = useState('');

  // Create Office Admin form state
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | ''>('');
  const [lastCreatedAdminEmail, setLastCreatedAdminEmail] = useState<string>('');

  const ministryNameFromUser = (user as any)?.ministry_name as string | undefined;

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const res = await ministriesAPI.getAll();
        const list: Array<{ id: number; name: string }> = (res as any).data || [];
        setMinistries(list);
        // Resolve ministry id from user.ministry_name if available
        const resolved = list.find(m => m.name === ministryNameFromUser);
        const id = resolved?.id ?? null;
        setMinistryId(id);
        if (id) {
          const officesRes = await officesAPI.byMinistry(id);
          setOffices(((officesRes as any).data || []) as OfficeItem[]);
        }
        if (id) {
          const adminsRes = await officesAPI.listAdmins(id);
          setAdmins(((adminsRes as any).data || []) as any[]);
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load offices');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [ministryNameFromUser]);

  const canCreate = useMemo(() => !!ministryId, [ministryId]);

  const handleCreateOffice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ministryId) return;
    try {
      const payload = { name: officeName.trim(), address: officeAddress.trim() || undefined, ministry_id: ministryId };
      if (!payload.name) {
        toast.error('Office name is required');
        return;
      }
      await officesAPI.create(payload);
      toast.success('Office created');
      setOfficeName('');
      setOfficeAddress('');
      const officesRes = await officesAPI.byMinistry(ministryId);
      setOffices(((officesRes as any).data || []) as OfficeItem[]);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to create office');
    }
  };

  const handleCreateOfficeAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ministryId || !selectedOfficeId) return;
    try {
      if (!adminPassword || adminPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (adminPassword !== adminPasswordConfirm) {
        toast.error('Passwords do not match');
        return;
      }
      const payload = {
        fullName: adminFullName.trim(),
        email: adminEmail.trim(),
        password: adminPassword,
        office_id: Number(selectedOfficeId),
        ministry_id: ministryId,
        phoneNumber: adminPhone.trim() || undefined,
      };
      if (!payload.fullName || !payload.email || !payload.password) {
        toast.error('All fields are required');
        return;
      }
      await officesAPI.createAdmin(payload);
      toast.success('Office admin created');
      setLastCreatedAdminEmail(payload.email);
      setAdminFullName('');
      setAdminEmail('');
      setAdminPassword('');
      setAdminPasswordConfirm('');
      setAdminPhone('');
      setSelectedOfficeId('');
      // refresh admins table
      const adminsRes = await officesAPI.listAdmins(ministryId);
      setAdmins(((adminsRes as any).data || []) as any[]);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to create office admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[200px] grid place-items-center">
        <div className="loading-spinner-xl"></div>
      </div>
    );
  }

  if (!ministryId) {
    return (
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Office Management</h2>
        <p className="text-sm text-gray-600">Could not resolve your ministry. Contact super admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold">Offices</h2>
        <div className="mt-4 overflow-hidden rounded-md border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {offices.map((o) => (
                <tr key={o.id}>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    <InlineEditText value={o.name} onSave={async (val) => {
                      if (!val || !val.trim()) return;
                      await officesAPI.update(o.id, { name: val.trim() });
                      toast.success('Updated');
                      const officesRes = await officesAPI.byMinistry(ministryId!);
                      setOffices(((officesRes as any).data || []) as OfficeItem[]);
                    }} />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    <InlineEditText value={o.address || ''} onSave={async (val) => {
                      await officesAPI.update(o.id, { address: val });
                      toast.success('Updated');
                      const officesRes = await officesAPI.byMinistry(ministryId!);
                      setOffices(((officesRes as any).data || []) as OfficeItem[]);
                    }} />
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {o.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-sm px-3 py-1 rounded-md border hover:bg-gray-50"
                      onClick={async () => {
                        await officesAPI.toggle(o.id);
                        toast.success('Status changed');
                        const officesRes = await officesAPI.byMinistry(ministryId!);
                        setOffices(((officesRes as any).data || []) as OfficeItem[]);
                      }}
                    >
                      {o.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {offices.length === 0 && (
                <tr>
                  <td className="px-4 py-4 text-sm text-gray-500" colSpan={2}>No offices yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <form onSubmit={handleCreateOffice} className="bg-white p-4 rounded-md border space-y-4">
          <h3 className="text-md font-semibold">Create Office</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">Office Name</label>
            <input value={officeName} onChange={e => setOfficeName(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="e.g. Planning Division" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <input value={officeAddress} onChange={e => setOfficeAddress(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="Optional" />
          </div>
          <button type="submit" disabled={!canCreate} className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">Create</button>
        </form>

        <form onSubmit={handleCreateOfficeAdmin} className="bg-white p-4 rounded-md border space-y-4">
          <h3 className="text-md font-semibold">Create Office Admin</h3>
          {lastCreatedAdminEmail && (
            <div className="rounded-md bg-green-50 text-green-800 text-sm px-3 py-2">
              Admin created. User ID (Email): <span className="font-medium">{lastCreatedAdminEmail}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input value={adminFullName} onChange={e => setAdminFullName(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">User ID (Email)</label>
            <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone (optional)</label>
            <input value={adminPhone} onChange={e => setAdminPhone(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Retype Password</label>
            <input type="password" value={adminPasswordConfirm} onChange={e => setAdminPasswordConfirm(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Office</label>
            <select value={selectedOfficeId} onChange={e => setSelectedOfficeId(e.target.value ? Number(e.target.value) : '')} className="mt-1 w-full border rounded-md px-3 py-2">
              <option value="">Select office</option>
              {offices.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={!canCreate || !selectedOfficeId} className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">Create Admin</button>
        </form>
      </div>

      {/* Admins table */}
      <div>
        <h2 className="text-lg font-semibold">Office Admins</h2>
        <div className="mt-4 overflow-hidden rounded-md border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID (Email)</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Office</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map(a => (
                <tr key={a.id}>
                  <td className="px-4 py-2 text-sm text-gray-900">{a.fullName}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{a.email}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{a.phoneNumber || '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{a.officeName || '-'}</td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td className="px-4 py-4 text-sm text-gray-500" colSpan={4}>No office admins yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Offices;

// lightweight inline edit component
const InlineEditText: React.FC<{ value: string; onSave: (val: string) => Promise<void> }> = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);

  if (!editing) {
    return (
      <span className="cursor-pointer hover:underline" onClick={() => setEditing(true)}>
        {value || '-'}
      </span>
    );
  }
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        await onSave(val);
        setEditing(false);
      }}
      className="flex items-center gap-2"
    >
      <input className="border rounded px-2 py-1 text-sm" value={val} onChange={(e) => setVal(e.target.value)} autoFocus />
      <button type="submit" className="text-sm px-2 py-1 border rounded hover:bg-gray-50">Save</button>
      <button type="button" className="text-sm px-2 py-1 border rounded hover:bg-gray-50" onClick={() => setEditing(false)}>Cancel</button>
    </form>
  );
};


